import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useFormState } from 'react-hook-form'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AddCommandsDialog, InternalIdInput, JsonEditor } from '@library'
import { MobileConfiguratorLayout } from '@mobileComponents/MobileConfiguratorLayout'
import { SaveDraftDialog } from '@mobileComponents/SaveDraftDialog'
import { TabPanel } from '@mobileComponents/TabPanel'
import {
  DEFAULT_MOBILE_LIST_ITEM,
  buildListItemScriptVariables,
  buildListItemScriptFieldsInfo,
} from '@mobileConstants'
import { extractServerErrorMessage, validateMobileListItem, convertSimpleToAdvanced } from '@mobileHelpers'
import { useDraft, useMobileListItems, useMobileGlobalVariables } from '@mobileHooks'
import { MobileListItemPreview } from '@mobilePreviews'
import { GETCommand, MobileListItem } from '@mobileTypes'
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'

import { useExecuteCommandMutation } from '@redux/api/command.api'

import { getCommandRequestData } from '@helpers'
import { ROUTES } from '@constants'

import { ActionsConfigTab } from '@mobileComponents/EventFlow'
import { AdvancedConfigTab } from './components/AdvancedConfigTab'
import { ConfigModeSwitchDialog } from './components/ConfigModeSwitchDialog'
import { DataSourceConfig } from './components/DataSourceConfig'
import { FabConfigSection } from './components/FabConfigSection'
import { IconConfigSection } from './components/IconConfigSection'
import { PrefilledVariablesConfig } from './components/PrefilledVariablesConfig'
import { RowsConfigTab } from './components/RowsConfigTab'
import { VariablesConfigTab } from '@mobileComponents/Variables'
import { v4 as uuidv4 } from 'uuid'
import { MOBILE_EVENT_CODE, MOBILE_ACTION_CODE } from '@mobileTypes'

interface PreviewCommandResult {
  key: string
  command: GETCommand
  rawData: any
  records: any[]
}

const COMMAND_KEY_PREFIX = 'command'

const getCommandKey = (index: number) => `${COMMAND_KEY_PREFIX}${index + 1}`

const ensureArray = (value: any): any[] => {
  if (value === undefined || value === null) {
    return []
  }

  return Array.isArray(value) ? value : [value]
}

const normalizeCommandRecords = (result: any): any[] => {
  if (result === undefined || result === null) {
    return []
  }

  if (Array.isArray(result)) {
    if (result.length > 0 && typeof result[0] === 'object' && result[0] !== null) {
      return result.map(entry =>
        entry && typeof entry === 'object' && 'object' in entry ? entry.object ?? entry : entry,
      )
    }

    return result
  }

  if (typeof result === 'object') {
    if (Array.isArray(result.items)) {
      return result.items
    }

    if (Array.isArray(result.data)) {
      return result.data
    }

    if (Array.isArray(result.rows)) {
      return result.rows
    }

    if ('object' in result && result.object) {
      return ensureArray(result.object)
    }
  }

  return ensureArray(result)
}

const buildSrcObj = (results: PreviewCommandResult[]): Record<string, any[]> => {
  return results.reduce((acc, entry) => {
    acc[entry.key] = entry.records

    if (entry.command.objectCode) {
      acc[entry.command.objectCode] = entry.records
    }

    return acc
  }, {} as Record<string, any[]>)
}

const evaluateUserCode = (code: string | undefined, context: Record<string, any>) => {
  const trimmed = (code || '').trim()
  if (!trimmed) {
    return undefined
  }

  const hasReturn = /return\b/.test(trimmed)
  const executable = hasReturn ? trimmed : `return (${trimmed});`

  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function(...Object.keys(context), executable)

    return fn(...Object.values(context))
  } catch (error) {
    if (error instanceof Error) {
      throw error
    }

    throw new Error(String(error))
  }
}

const extractErrorMessage = (error: unknown): string => {
  if (!error) {
    return 'Неизвестная ошибка при выполнении команды'
  }

  if (typeof error === 'string') {
    return error
  }

  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'object') {
    const errObj = error as Record<string, any>

    if (errObj.data) {
      const data = errObj.data
      if (typeof data === 'string') {
        return data
      }

      if (data && typeof data === 'object') {
        return data.message || data.error || data.details || JSON.stringify(data)
      }
    }

    if (typeof errObj.message === 'string') {
      return errObj.message
    }
  }

  return 'Не удалось выполнить команду'
}

const derivePreviewItems = (listItem: MobileListItem, results: PreviewCommandResult[]): any[] => {
  if (!results.length) {
    return []
  }

  const dataSource = listItem?.dataSource
  const srcObj = buildSrcObj(results)

  if (dataSource?.bindType === 'command') {
    if (dataSource.commandKey) {
      const matched = results.find(result => result.key === dataSource.commandKey)
      if (matched) {
        return matched.records
      }
    }

    return results[0].records
  }

  if (dataSource?.bindType === 'js' && dataSource.jsCode) {
    const output = evaluateUserCode(dataSource.jsCode, srcObj)

    if (output === undefined || output === null) {
      return []
    }

    return Array.isArray(output) ? output : [output]
  }

  return results[0].records
}

// Migrate onItemPress to eventFlows format
const migrateOnItemPressToEventFlows = (item: MobileListItem): MobileListItem => {
  // If already has eventFlows or no onItemPress, return as is
  if ((item.eventFlows && item.eventFlows.length > 0) || !item.onItemPress) {
    return item
  }

  // Create a new event flow for ON_LIST_ITEM_PRESS
  const newEventFlow = {
    id: uuidv4(),
    eventCode: MOBILE_EVENT_CODE.ON_LIST_ITEM_PRESS,
    actions: [
      {
        id: 'migrated_on_list_item_press',
        actionCode: MOBILE_ACTION_CODE.NAVIGATE, // Default to navigate for onItemPress
        dependsOn: undefined,
      },
    ],
  }

  return {
    ...item,
    eventFlows: [newEventFlow],
  }
}

export const MobileListItemCreateOrEdit = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = !!id

  const { listItems, createListItem, updateListItem, getListItemById } = useMobileListItems()
  const { saveDraft, clearDraft, draft, hasDraft } = useDraft<MobileListItem>(`mobile-list-item-draft-${id ?? 'new'}`)
  const shouldAutoRestore = useRef((location.state as any)?.restoreDraft === true)
  const [tabValue, setTabValue] = useState(0)
  const [jsonValue, setJsonValue] = useState('')
  const [showCommandsDialog, setShowCommandsDialog] = useState(false)
  const [modeSwitchDialog, setModeSwitchDialog] = useState<{
    open: boolean
    currentMode: 'simple' | 'advanced'
    targetMode: 'simple' | 'advanced'
  } | null>(null)

  const methods = useForm<MobileListItem>({
    defaultValues: isEdit
      ? getListItemById(id) || DEFAULT_MOBILE_LIST_ITEM
      : DEFAULT_MOBILE_LIST_ITEM,
  })

  const { control, handleSubmit, reset, watch } = methods
  const [executeCommand] = useExecuteCommandMutation()

  const formData = watch()
  const { isDirty } = useFormState({ control })
  const { variables: globalVariables } = useMobileGlobalVariables()

  const listItemScriptVariables = useMemo(
    () => buildListItemScriptVariables(formData.variables || [], globalVariables),
    [formData.variables, globalVariables]
  )
  const listItemScriptFieldsInfo = useMemo(
    () => buildListItemScriptFieldsInfo(formData.variables || [], globalVariables),
    [formData.variables, globalVariables]
  )

  const [previewData, setPreviewData] = useState<any[]>([])
  const [customPreviewData, setCustomPreviewData] = useState<any[] | null>(null)
  const [previewCommandResults, setPreviewCommandResults] = useState<Record<string, any[]>>({})
  const [previewCommandMeta, setPreviewCommandMeta] = useState<PreviewCommandResult[]>([])
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  // Обработчик запроса на переключение режима
  const handleModeChangeRequest = useCallback(
    (newMode: 'simple' | 'advanced') => {
      const currentMode = (formData.configMode || 'simple') as 'simple' | 'advanced'

      if (currentMode === newMode) return

      // Показать диалог подтверждения
      setModeSwitchDialog({
        open: true,
        currentMode,
        targetMode: newMode,
      })
    },
    [formData.configMode],
  )

  // Обработчик подтверждения переключения режима
  const handleModeChangeConfirm = useCallback(
    (shouldConvert: boolean) => {
      if (!modeSwitchDialog) return

      const { targetMode } = modeSwitchDialog

      if (targetMode === 'advanced') {
        // Переход из Simple в Advanced
        if (shouldConvert) {
          // Конвертировать Simple данные в Advanced
          const converted = convertSimpleToAdvanced({
            rows: formData.rows || [],
            icon: formData.icon,
          } as MobileListItem)

          methods.setValue('itemTemplate', converted || { children: [] }, { shouldDirty: true })
        } else {
          // Начать с чистого листа
          methods.setValue('itemTemplate', { children: [] }, { shouldDirty: true })
        }

        // Очистить Simple данные
        methods.setValue('rows', [], { shouldDirty: true })
        methods.setValue('icon', { value: '', isJS: false }, { shouldDirty: true })
      } else {
        // Переход из Advanced в Simple
        // Удалить Advanced данные
        methods.setValue('itemTemplate', { children: [] }, { shouldDirty: true })

        // Очистить Simple данные тоже (начинаем с чистого листа)
        methods.setValue('rows', [], { shouldDirty: true })
        methods.setValue('icon', { value: '', isJS: false }, { shouldDirty: true })
      }

      // Переключить режим
      methods.setValue('configMode', targetMode, { shouldDirty: true })

      // Закрыть диалог
      setModeSwitchDialog(null)
    },
    [modeSwitchDialog, formData.rows, formData.icon, methods],
  )

  // Обработчик отмены переключения режима
  const handleModeChangeCancel = useCallback(() => {
    setModeSwitchDialog(null)
  }, [])

  useEffect(() => {
    if (!isEdit && shouldAutoRestore.current && draft) {
      Object.keys(draft.data).forEach(key => {
        methods.setValue(key as any, (draft.data as any)[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(JSON.stringify(draft.data, null, 2))
      shouldAutoRestore.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEdit && id) {
      const item = getListItemById(id)
      if (item) {
        if (shouldAutoRestore.current && draft) {
          Object.keys(draft.data).forEach(key => {
            methods.setValue(key as any, (draft.data as any)[key], { shouldDirty: true, shouldValidate: false })
          })
          setJsonValue(JSON.stringify(draft.data, null, 2))
          shouldAutoRestore.current = false
        } else {
          const migratedItem = migrateOnItemPressToEventFlows(item)
          reset(migratedItem)
          setJsonValue(JSON.stringify(migratedItem, null, 2))
        }
      }
    }
  }, [id, isEdit, getListItemById, reset])

  useEffect(() => {
    if (tabValue === 2) {
      setJsonValue(JSON.stringify(formData, null, 2))
    }
  }, [formData, tabValue])

  const [errors, setErrors] = useState<string[]>([])
  const [saveError, setSaveError] = useState<string | null>(null)

  const onSubmit = async (data: MobileListItem) => {
    const validationErrors = validateMobileListItem(data)

    if (!isEdit) {
      const duplicate = listItems.find(item => item.internalId === data.internalId?.trim())
      if (duplicate) {
        validationErrors.push('A config with this Internal ID already exists')
      }
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors([])

    // Ensure itemType is always 'dictionary'
    const itemData = {
      ...data,
      itemType: 'dictionary',
    }

    try {
      if (isEdit && id) {
        const currentItem = getListItemById(id)
        if (currentItem) {
          const updatedItem: MobileListItem = {
            ...itemData,
            id: currentItem.id,
            createdAt: currentItem.createdAt,
            updatedAt: Date.now(),
          }
          await updateListItem(id, itemData)
          reset(updatedItem)
          setJsonValue(JSON.stringify(updatedItem, null, 2))
          clearDraft()
        } else {
          await updateListItem(id, itemData)
          clearDraft()
        }
      } else {
        const newItem = await createListItem(itemData)
        clearDraft()
        navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${newItem.id}`, { replace: true })
      }
    } catch (error: unknown) {
      setSaveError(extractServerErrorMessage(error))
    }
  }

  const handleCancel = () => {
    navigate(ROUTES.CONFIG_MOBILE_LIST_ITEMS)
  }

  const handleReset = () => {
    if (isEdit && id) {
      const item = getListItemById(id)
      if (item) {
        const migratedItem = migrateOnItemPressToEventFlows(item)
        methods.reset(migratedItem)
        setJsonValue(JSON.stringify(migratedItem, null, 2))
        setErrors([])
      }
    } else {
      methods.reset(DEFAULT_MOBILE_LIST_ITEM)
      setJsonValue(JSON.stringify(DEFAULT_MOBILE_LIST_ITEM, null, 2))
      setErrors([])
    }
  }

  const handleJsonChange = (newValue: string) => {
    try {
      const parsed = JSON.parse(newValue)
      // Use setValue for each field to mark form as dirty
      Object.keys(parsed).forEach(key => {
        methods.setValue(key as any, parsed[key], { shouldDirty: true, shouldValidate: false })
      })
      setJsonValue(newValue)
    } catch (error) {
      // Invalid JSON, just update the state
      setJsonValue(newValue)
    }
  }

  const handleOpenCommandsDialog = () => {
    setShowCommandsDialog(true)
  }

  const handleCloseCommandsDialog = () => {
    setShowCommandsDialog(false)
  }

  const handleChangeCommands = (commands?: GETCommand[] | null) => {
    methods.setValue('getDataCommands', commands, { shouldDirty: true })
  }

  const watchCommands = methods.watch('getDataCommands')

  const previewCommandResultsByObject = useMemo(() => {
    return previewCommandMeta.reduce((acc, entry) => {
      if (entry.command.objectCode) {
        acc[entry.command.objectCode] = entry.records
      }

      return acc
    }, {} as Record<string, any[]>)
  }, [previewCommandMeta])

  const handleRefreshPreview = useCallback(async () => {
    const currentValues = methods.getValues()
    const commands = currentValues.getDataCommands ?? []

    if (!commands.length) {
      setPreviewError('Добавьте хотя бы одну команду для предварительного просмотра')

      return
    }

    // Reset custom data when refreshing from API
    setCustomPreviewData(null)
    setPreviewLoading(true)
    setPreviewError(null)

    const settled = await Promise.allSettled(
      commands.map(async (command, index) => {
        const key = getCommandKey(index)
        const commandLabel = command.name || key

        if (!command.name || !command.objectCode) {
          throw new Error(
            `Команда "${commandLabel}" не содержит необходимые параметры (name или objectCode)`,
          )
        }

        const params = getCommandRequestData(command.parameters ?? [], {})
        const body = getCommandRequestData(command.body ?? [], {})

        try {
          const response = await executeCommand({
            commandCode: command.name,
            objectCode: command.objectCode,
            method: command.httpMethod || 'POST',
            params,
            body,
          }).unwrap()

          const records = normalizeCommandRecords(response)

          return {
            key,
            command,
            rawData: response,
            records,
          } as PreviewCommandResult
        } catch (error) {
          throw new Error(
            `Ошибка при выполнении команды "${commandLabel}": ${extractErrorMessage(error)}`,
          )
        }
      }),
    )

    const rejectedResult = settled.find(
      (result): result is PromiseRejectedResult => result.status === 'rejected',
    )

    if (rejectedResult) {
      setPreviewError(extractErrorMessage(rejectedResult.reason))
      setPreviewLoading(false)

      return
    }

    const fulfilledResults = settled as PromiseFulfilledResult<PreviewCommandResult>[]
    const results = fulfilledResults.map(result => result.value)

    const srcObjMap = results.reduce((acc, entry) => {
      acc[entry.key] = entry.records

      if (entry.command.objectCode) {
        acc[entry.command.objectCode] = entry.records
      }

      return acc
    }, {} as Record<string, any[]>)

    try {
      const primaryItems = derivePreviewItems(currentValues as MobileListItem, results)

      setPreviewCommandResults(srcObjMap)
      setPreviewCommandMeta(results)
      setPreviewData(primaryItems)
      setPreviewError(null)
    } catch (error) {
      setPreviewError(extractErrorMessage(error))
    } finally {
      setPreviewLoading(false)
    }
  }, [executeCommand, methods])

  const handleDataEdit = useCallback((newData: any[]) => {
    setCustomPreviewData(newData)
  }, [])

  // Preview для Config вкладки
  const previewComponent = useMemo(() => {
    if (tabValue === 1) {
      return (
        <MobileListItemPreview
          item={formData}
          data={customPreviewData ?? previewData}
          onRefresh={handleRefreshPreview}
          onDataEdit={handleDataEdit}
          isLoading={previewLoading}
          error={previewError}
          commandResultsByKey={previewCommandResults}
          commandResultsByObject={previewCommandResultsByObject}
        />
      )
    }

    return undefined
  }, [
    tabValue,
    formData,
    previewData,
    customPreviewData,
    handleRefreshPreview,
    handleDataEdit,
    previewLoading,
    previewError,
    previewCommandResults,
    previewCommandResultsByObject,
  ])

  return (
    <>
      <SaveDraftDialog
        open={!!saveError}
        errorMessage={saveError ?? ''}
        onSaveDraft={() => saveDraft(methods.getValues())}
        onClose={() => setSaveError(null)}
      />
      <MobileConfiguratorLayout
        title={isEdit ? 'Edit List Item Config' : 'Create List Item Config'}
        isEdit={isEdit}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={handleCancel}
        onReset={handleReset}
        isDirty={isDirty}
        errors={errors}
        previewComponent={previewComponent}
        draftProps={{
          hasDraft,
          draftSavedAt: draft?.savedAt,
          onRestoreDraft: () => {
            if (draft) {
              methods.reset(draft.data)
              setJsonValue(JSON.stringify(draft.data, null, 2))
            }
          },
          onDiscardDraft: clearDraft,
          onSaveDraft: () => saveDraft(methods.getValues()),
        }}
      >
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Basic Info" />
          <Tab label="Config" />
          <Tab label="Variables" />
          <Tab label="Actions" />
          <Tab label="JSON Editor" />
        </Tabs>
      </Paper>

      <FormProvider {...methods}>
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name='internalId'
                control={control}
                rules={{
                  required: 'Internal ID is required',
                  validate: value => {
                    if (!value?.trim() || isEdit) return true
                    const duplicate = listItems.find(
                      item => item.internalId === value.trim(),
                    )
                    return duplicate
                      ? 'A config with this Internal ID already exists'
                      : true
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <InternalIdInput
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    fullWidth
                    label='Internal ID'
                    error={!!error}
                    helperText={error?.message}
                    required
                    disabled={isEdit}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Name is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Config Name"
                    error={!!error}
                    helperText={error?.message}
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">Get Data Commands</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {watchCommands?.length || 0} command(s)
                  </Typography>
                </Box>
                <Button variant="outlined" fullWidth onClick={handleOpenCommandsDialog}>
                  {watchCommands?.length ? 'Edit Commands' : 'Add Commands'}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <DataSourceConfig getDataCommands={formData.getDataCommands || null} />
            </Grid>

            {formData.dataSource?.bindType === 'prefilled' && <Grid item xs={12}>
              <PrefilledVariablesConfig />
            </Grid>}

            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant='subtitle1' gutterBottom>Search Configuration</Typography>
                <Controller
                  name='enableSearch'
                  control={control}
                  render={({ field }) => (
                    <Tooltip title='Show a search bar above the list. Requires at least one command data source and searchFields.'>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        }
                        label='Enable Search'
                      />
                    </Tooltip>
                  )}
                />
                {formData.enableSearch && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant='body2' color='text.secondary' gutterBottom>
                      Search Fields (press Enter to add)
                    </Typography>
                    <Controller
                      name='searchFields'
                      control={control}
                      defaultValue={[]}
                      render={({ field }) => {
                        const [inputValue, setInputValue] = useState('')
                        const fields: string[] = field.value || []
                        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            const trimmed = inputValue.trim()
                            if (trimmed && !fields.includes(trimmed)) {
                              field.onChange([...fields, trimmed])
                            }
                            setInputValue('')
                          }
                        }
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <TextField
                              size='small'
                              placeholder='e.g. model, reg_number'
                              value={inputValue}
                              onChange={e => setInputValue(e.target.value)}
                              onKeyDown={handleKeyDown}
                              helperText='Field names used in RSQL filter (type and press Enter)'
                            />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {fields.map(f => (
                                <Chip
                                  key={f}
                                  label={f}
                                  size='small'
                                  onDelete={() => field.onChange(fields.filter(x => x !== f))}
                                />
                              ))}
                            </Box>
                          </Box>
                        )
                      }}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>



            {showCommandsDialog && (
              <AddCommandsDialog
                objectCode=""
                commands={watchCommands}
                onCloseDialog={handleCloseCommandsDialog}
                onChangeCommands={handleChangeCommands}
                commandPickerControllerProps={{
                  isTypeHidden: true,
                }}
                scriptVariables={listItemScriptVariables}
                scriptFieldsInfo={listItemScriptFieldsInfo}
              />
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="subtitle1">Configuration Mode</Typography>
                <Controller
                  name="configMode"
                  control={control}
                  defaultValue="simple"
                  render={({ field }) => (
                    <ToggleButtonGroup
                      value={field.value || 'simple'}
                      exclusive
                      onChange={(_, value) => {
                        if (value !== null) {
                          handleModeChangeRequest(value as 'simple' | 'advanced')
                        }
                      }}
                      size="small"
                    >
                      <ToggleButton value="simple">Simple</ToggleButton>
                      <ToggleButton value="advanced">Advanced</ToggleButton>
                    </ToggleButtonGroup>
                  )}
                />
              </Box>
            </Paper>

            {formData.configMode === 'advanced' ? (
              <AdvancedConfigTab />
            ) : (
              <>
                <Paper sx={{ p: 2 }}>
                  <IconConfigSection />
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <FabConfigSection />
                </Paper>
                <RowsConfigTab />
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <VariablesConfigTab />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <ActionsConfigTab />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <JsonEditor
            value={jsonValue}
            onChange={handleJsonChange}
            label="List Item Configuration (JSON)"
            minHeight={400}
          />
        </TabPanel>
      </FormProvider>

      {/* Config Mode Switch Dialog */}
      {modeSwitchDialog && (
        <ConfigModeSwitchDialog
          open={modeSwitchDialog.open}
          currentMode={modeSwitchDialog.currentMode}
          targetMode={modeSwitchDialog.targetMode}
          onConfirm={handleModeChangeConfirm}
          onCancel={handleModeChangeCancel}
        />
      )}
    </MobileConfiguratorLayout>
    </>
  )
}
